import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchCadeCommissioners } from '@/services/cade';
import { Stat } from '@/pages/cade/Stat';
import type { CadeBench, CadeBenchMember, CadeRapporteurCaseload } from '@/services/types';

/**
 * CADE rapporteurs, counted and never rated.
 *
 * There is no rate on this page and none should be added. ML blueprint Part 15.2
 * is explicit that a raw outcome rate is not a measure of strictness, and this
 * corpus shows why rather than asserting it: over comparable caseloads, one
 * rapporteur's final decisions include twelve appeals and five settlement
 * requests, another's include one appeal and thirteen settlement requests. Those
 * dockets cannot resolve the same way.
 *
 * So the case-type mix sits BESIDE the outcome mix instead of being divided out
 * of it — nothing in this corpus supports the adjustment, and an unadjusted rate
 * presented as one would rank dockets while appearing to rank judgement.
 *
 * The two analyses a reader will actually come here for — do commissioners agree
 * with each other, and do they follow the rapporteur — are still refused, and the
 * refusals are reused verbatim from the overview rather than re-derived.
 */
export function CadeCommissionersPage() {
  const { t } = useTranslation(['cade', 'common']);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['cade', 'commissioners'],
    queryFn: fetchCadeCommissioners,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:commissioners.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:commissioners.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cade:commissioners.explainer" />

      <FieldGuide i18nKey="cade:commissioners.guide" level={2} />

      <AsOfStrip asOf={data.as_of} />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/cade/export/commissioners" />
      </div>

      {/* The bench itself, before the rapporteur table: everyone the corpus
          names in a decision-making role, with the years they appear in. The
          table below it covers only those who relate ten or more final
          decisions, so a reader who arrives asking "who sits on this
          tribunal, and since when" is answered here first. */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('cade:commissioners.roster.title')}
        </h2>
        <Answer answer={data.roster} help={t('cade:commissioners.roster.help')}>
          {(members) => <RosterTable members={members} />}
        </Answer>
        <FieldGuide i18nKey="cade:commissioners.rosterGuide" />
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('cade:commissioners.caseloads.title')}
        </h2>
        <Answer answer={data.bench} help={t('cade:commissioners.help')}>
          {(value) => <BenchTable bench={value} />}
        </Answer>
      </section>

      {/* The two questions a reader came for, both still unanswerable. Given
          the same weight as the table above: a refusal carrying its denominator
          is more useful than a caseload count, and burying it would leave the
          counts looking like the answer. */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('cade:commissioners.refusals.title')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('cade:commissioners.refusals.intro')}
        </p>
        {/* Both carry a pointer, as their twins on `/cade/overview` do. The
            ANSWERS were reused verbatim from that screen and the `elsewhere`
            props were not, so these two rendered as a bare heading over a reason
            -- a refusal with nowhere to go, while the rows behind it sit one
            click away. The destinations differ from the overview's precisely
            because the reader is already HERE: the caseload pointer aims at the
            table above rather than at this page, and the agreement pointer at
            `/cade/votes`, which did not exist when the overview's was written. */}
        <div className="grid gap-4 lg:grid-cols-2">
          <RefusalCard
            title={t('cade:overview.refusals.agreement')}
            answer={data.agreement_matrix}
            elsewhere={
              <Link to="/cade/votes" className="text-primary underline-offset-4 hover:underline">
                {t('cade:commissioners.refusals.agreementVotes')}
              </Link>
            }
          />
          <RefusalCard
            title={t('cade:overview.refusals.followRate')}
            answer={data.rapporteur_follow_rate}
            elsewhere={t('cade:commissioners.refusals.followRateCaseloads')}
          />
        </div>
      </section>
    </div>
  );
}

/**
 * One row per person on the bench, ordered by first year then name upstream.
 *
 * Years and counts only. Recusals are shown BESIDE votes rather than divided
 * out of them: a member whose every minuted position is a recusal reads as
 * exactly that, and a share would turn it into a figure about the person.
 */
function RosterTable({ members }: { members: CadeBenchMember[] }) {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const columns = ['tenure', 'sessions', 'relatorFinal', 'votes', 'recused'] as const;

  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">{t('cade:commissioners.roster.columns.name')}</th>
            {columns.map((column) => (
              <th
                key={column}
                className={`px-3 py-2 font-medium ${column === 'tenure' ? '' : 'text-right'}`}
              >
                {t(`cade:commissioners.roster.columns.${column}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.slug} className="border-t hover:bg-muted/30">
              <td className="px-3 py-2">
                <Link
                  to={`/cade/r/${member.slug}`}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {member.name}
                </Link>
              </td>
              <td className="px-3 py-2 tabular-nums text-foreground">
                {member.first_year !== null && member.last_year !== null
                  ? t('common:asOf.yearWindow', { first: member.first_year, last: member.last_year })
                  : '—'}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                {number.format(member.sessions)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-foreground">
                {number.format(member.relator_final)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-foreground">
                {number.format(member.votes)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                {number.format(member.recused)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BenchTable({ bench }: { bench: CadeBench }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <Stat label={t('cade:commissioners.stats.people')} value={number.format(bench.people)} />
        <Stat
          label={t('cade:commissioners.stats.shown')}
          value={`${number.format(bench.rapporteurs.length)} / ${number.format(bench.people)}`}
        />
        <Stat
          label={t('cade:commissioners.stats.attributed')}
          value={`${number.format(bench.final_with_rapporteur)} / ${number.format(
            bench.final_decisions,
          )}`}
        />
        <Stat
          label={t('cade:commissioners.stats.threshold')}
          value={number.format(bench.threshold)}
        />
      </dl>

      <ul className="space-y-2">
        {bench.rapporteurs.map((rapporteur) => (
          <RapporteurRow key={rapporteur.name} rapporteur={rapporteur} />
        ))}
      </ul>

      {bench.suppressed > 0 ? (
        <p className="rounded border border-dashed bg-muted/30 p-2 text-xs text-muted-foreground">
          {t('cade:commissioners.suppressed', {
            suppressed: number.format(bench.suppressed),
            threshold: number.format(bench.threshold),
          })}
        </p>
      ) : null}

      {/* The casing variant is folded as a grouping key only (`case_type_norm_sql`,
          the same key the case list and the search match on); the stored value
          is untouched and the note says so. */}
      <p className="text-xs text-muted-foreground">{t('cade:commissioners.rawTypes')}</p>
    </div>
  );
}

/**
 * One rapporteur.
 *
 * Ordered by caseload, and deliberately not by any outcome. A table sorted by
 * "condemned" would be read as a ranking of severity within a second of landing
 * on the page, whatever the caveats above it say.
 */
function RapporteurRow({ rapporteur }: { rapporteur: CadeRapporteurCaseload }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (
    <li className="space-y-2 rounded-lg border bg-card p-3 text-xs">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Link
          to={`/cade/r/${rapporteur.slug}`}
          className="text-sm text-primary underline-offset-4 hover:underline"
        >
          {rapporteur.name}
        </Link>
        <span className="shrink-0 tabular-nums text-muted-foreground">
          {t('cade:commissioners.caseload', {
            final: number.format(rapporteur.final_decisions),
            all: number.format(rapporteur.decisions),
          })}
          {/* The window the count is read against. 40 across three years and
              11 in one year sit in the same list, and without this they read
              as comparable. */}
          {rapporteur.first_year !== null && rapporteur.last_year !== null ? (
            <span className="ml-2">
              · {t('common:asOf.yearWindow', { first: rapporteur.first_year, last: rapporteur.last_year })}
            </span>
          ) : null}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <p className="mb-1 font-medium text-foreground">
            {t('cade:commissioners.caseTypes', {
              concentration: percent.format(rapporteur.concentration),
            })}
          </p>
          <Bars items={rapporteur.case_types} total={rapporteur.final_decisions} />
        </div>
        <div>
          <p className="mb-1 font-medium text-foreground">
            {t('cade:commissioners.outcomes', {
              classified: number.format(rapporteur.classified),
              final: number.format(rapporteur.final_decisions),
            })}
          </p>
          {rapporteur.outcomes.length > 0 ? (
            <Bars
              items={rapporteur.outcomes}
              total={rapporteur.classified}
              labelFor={(code) => t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
            />
          ) : (
            <p className="text-muted-foreground">{t('cade:commissioners.noOutcomes')}</p>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * Counts with their share of a STATED total, never a bare percentage.
 *
 * `labelFor` turns the API's label into words. Outcomes arrive as ETL codes
 * (`TCC_HOMOLOGATED`) and must be translated; case types arrive as the
 * registry's own Portuguese text and are shown as written.
 */
function Bars({
  items,
  total,
  labelFor = (label) => label,
}: {
  items: { label: string; decisions: number }[];
  total: number;
  labelFor?: (label: string) => string;
}) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const shown = items.slice(0, BARS_SHOWN);

  return (
    <ul className="space-y-1">
      {shown.map((item) => (
        <li key={item.label} className="flex items-baseline justify-between gap-2">
          <span className="truncate text-muted-foreground" title={labelFor(item.label)}>
            {labelFor(item.label)}
          </span>
          <span className="shrink-0 tabular-nums text-foreground">
            {number.format(item.decisions)}
            <span className="text-muted-foreground">
              {total > 0 ? ` / ${number.format(total)}` : ''}
            </span>
          </span>
        </li>
      ))}
      {/* Said, not silently cut: a list truncated without a count reads as
          the whole list. */}
      {items.length > shown.length ? (
        <li className="text-muted-foreground">
          {t('cade:commissioners.moreBars', { count: items.length - shown.length })}
        </li>
      ) : null}
    </ul>
  );
}

/** Rows shown before a "+N" line, on both pages that draw these bars. */
const BARS_SHOWN = 6;

